/**
 * The Discover layout as the page holds it (#2291): the document the server
 * last confirmed, plus the changes not yet stored, applied on top so the page
 * shows them at once (optimistic), and dropped again if their write fails.
 *
 * Writes:
 * - each is ONE `PUT` of the whole document, at the `rev` it was read, and
 *   the first freezes a preset into `custom` (any change by hand is custom;
 *   choosing a preset or resetting names its own, `LayoutChange.setsPreset`);
 * - a change made with a `key` (a count being typed, a drag, Move up pressed
 *   again and again) waits `debounceMs` before it is sent, and another change
 *   with the same key in that time joins it: one write for the run;
 * - Reset is the server's reset (`POST …/reset`, #2298): the default
 *   preset's sections, the saved searches kept, against the `rev` read like
 *   any write;
 * - one is in flight at a time; the next waits, then applies to whatever the
 *   one before it left (so two quick clicks never race each other's `rev`);
 * - a 409 hands back the server's current document: the same change is
 *   applied to it and sent once more; a second 409 gives up with a plain
 *   message, showing the server's document;
 * - a 400 comes back as the server's problems in plain words, a network or
 *   server failure as a message; either way the change is dropped (rollback).
 *
 * Loading: a copy of the last confirmed document is kept per profile
 * (`localStorage`), so a returning visitor's page draws its own layout on
 * the first frame, and the answer only replaces it when another device
 * changed it. With no copy (a first visit), the sections hold for at most
 * `holdMs` for the answer, then draw the built-in default: someone with no
 * stored layout sees today's page, never a reshuffle from default to
 * theirs. Writes wait for the load, so they always start from the server's
 * document when there is one.
 *
 * A document on a preset is read through `resolveLayout` (a preset is a
 * living default): the page, and every change, see the preset's sections as
 * the server defines them, in the registry the document came with.
 */
import {
  DiscoverLayoutInvalidError,
  DISCOVER_SCHEMA,
  type DiscoverLayout,
  type DiscoverPutResult,
} from '@/api/discoverPreferences';
import {
  DEFAULT_LAYOUT,
  LayoutChangeError,
  explainLayoutProblems,
  resetLayout,
  resolveLayout,
  sameLayout,
  type LayoutChange,
} from './discoverLayout';

export type WriteOutcome =
  | { ok: true; layout: DiscoverLayout }
  /** The server refused the document: why, in plain words. */
  | { ok: false; reason: 'invalid'; problems: string[] }
  /** The change cannot apply (a limit, or its subject is gone). */
  | { ok: false; reason: 'refused'; message: string }
  /** Still stale after reloading once. */
  | { ok: false; reason: 'conflict'; message: string }
  | { ok: false; reason: 'failed'; message: string };

/** What a failed write says, in plain words. */
export function outcomeMessages(outcome: WriteOutcome): string[] {
  if (outcome.ok) return [];
  return outcome.reason === 'invalid' ? outcome.problems : [outcome.message];
}

export interface LayoutSnapshot {
  /** What the page shows: confirmed plus pending changes. */
  layout: DiscoverLayout;
  /** No copy yet and no answer: the sections wait rather than draw a layout that may be replaced. */
  holding: boolean;
  /** A write is in flight or queued. */
  saving: boolean;
}

export interface LayoutCache {
  read(): DiscoverLayout | null;
  write(layout: DiscoverLayout): void;
}

export interface LayoutStoreDeps {
  get: () => Promise<DiscoverLayout>;
  put: (layout: DiscoverLayout) => Promise<DiscoverPutResult>;
  /** `POST …/reset`: the default preset back, the saved searches kept, from `rev`. */
  reset: (rev: number) => Promise<DiscoverPutResult>;
  cache: LayoutCache;
  /** How long a first visit waits for the answer before drawing the default. */
  holdMs?: number;
  /** How long a keyed change waits for the next one with its key. */
  debounceMs?: number;
}

export const HOLD_MS = 400;
export const DEBOUNCE_MS = 600;

export interface ApplyOptions {
  /** Changes with the same key, made within `debounceMs` of each other, are sent as one write. */
  key?: string;
}

export const CONFLICT_MESSAGE =
  'Your Discover page was changed on another device at the same time, so this change was not saved. It now shows the latest version; try again.';
/** The Customize sheet's word for a conflict: the change was not made, and the page shows the newer one. */
export const RELOADED_MESSAGE = 'Discover changed on another device — reloaded. Make your change again.';
export const FAILED_MESSAGE = "Couldn't save your Discover page. Check your connection and try again.";

/** Holds a keyed change back until no other with its key has come for a while. */
interface Gate {
  wait: Promise<void>;
  bump: () => void;
  open: () => void;
}

function gate(ms: number): Gate {
  let release!: () => void;
  const wait = new Promise<void>((r) => { release = r; });
  let timer = setTimeout(release, ms);
  return {
    wait,
    bump: () => { clearTimeout(timer); timer = setTimeout(release, ms); },
    open: () => { clearTimeout(timer); release(); },
  };
}

interface Pending {
  change: LayoutChange;
  key?: string;
  gate?: Gate;
  /** Its write has started: no later change joins it. */
  sent: boolean;
  /** Reset: sent as the server's reset, not a PUT. */
  reset?: boolean;
  outcome?: Promise<WriteOutcome>;
}

/** Two changes as one: the first, then the second; a preset either one chooses is kept. */
function both(first: LayoutChange, second: LayoutChange): LayoutChange {
  const change: LayoutChange = (layout) => second(first(layout));
  const preset = second.setsPreset ?? first.setsPreset;
  if (preset) change.setsPreset = preset;
  return change;
}

/**
 * A change applied as the store stores it: a change by hand leaves the
 * document on `custom`, one that chooses a layout on its preset.
 */
function changed(layout: DiscoverLayout, change: LayoutChange): DiscoverLayout {
  const next = change(layout);
  if (change.setsPreset) return { ...next, preset: change.setsPreset };
  return sameLayout({ ...next, preset: layout.preset }, layout) ? next : { ...next, preset: 'custom' };
}

/** Applies a change to the document as it is now, keeping it when the change no longer applies. */
function tryApply(layout: DiscoverLayout, change: LayoutChange): DiscoverLayout {
  try {
    return changed(layout, change);
  } catch (err) {
    if (err instanceof LayoutChangeError) return layout;
    throw err;
  }
}

export class DiscoverLayoutStore {
  private confirmed: DiscoverLayout | null;
  private pending: Pending[] = [];
  private holding: boolean;
  private snapshot!: LayoutSnapshot;
  private readonly listeners = new Set<() => void>();
  private loading: Promise<void> | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly deps: LayoutStoreDeps;

  constructor(deps: LayoutStoreDeps) {
    this.deps = deps;
    this.confirmed = deps.cache.read();
    this.holding = !this.confirmed;
    this.recompute(false);
  }

  /** Ask for the layout (once), and start the first visit's wait. */
  start = (): void => {
    if (!this.loading) {
      this.loading = this.deps.get().then(
        (layout) => this.confirm(layout),
        () => undefined,
      ).finally(() => this.release());
      this.queue = this.loading;
    }
    if (this.holding && !this.timer) {
      this.timer = setTimeout(() => this.release(), this.deps.holdMs ?? HOLD_MS);
    }
  };

  /** Stop waiting: the first visit's hold ends, and changes held back for more of their kind go now. */
  stop = (): void => {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.flush();
  };

  /** Send the changes held back for more of their kind now (the sheet closed, the page left). */
  flush = (): void => {
    this.pending.forEach((p) => p.gate?.open());
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): LayoutSnapshot => this.snapshot;

  /**
   * Make a change: shown at once, stored in the background. Resolves with
   * what came of it; never rejects.
   */
  apply = (change: LayoutChange, options: ApplyOptions = {}): Promise<WriteOutcome> => {
    try {
      change(this.snapshot.layout);
    } catch (err) {
      if (err instanceof LayoutChangeError) return Promise.resolve({ ok: false, reason: 'refused', message: err.message });
      throw err;
    }
    const last = this.pending.at(-1);
    if (options.key && last && !last.sent && last.key === options.key && last.outcome) {
      // the same kind of change again, before the last one went: one write for both
      last.change = both(last.change, change);
      last.gate?.bump();
      this.recompute();
      return last.outcome;
    }
    return this.enqueue({
      change,
      key: options.key,
      gate: options.key ? gate(this.deps.debounceMs ?? DEBOUNCE_MS) : undefined,
      sent: false,
    });
  };

  /**
   * Reset to the default preset, keeping the saved searches: shown at once,
   * then the server's reset, retried once on a 409 like any write. Never
   * rejects.
   */
  reset = (): Promise<WriteOutcome> => this.enqueue({ change: resetLayout(), sent: false, reset: true });

  private enqueue(entry: Pending): Promise<WriteOutcome> {
    this.pending = [...this.pending, entry];
    this.recompute();
    const run = this.queue.then(() => entry.gate?.wait).then(() => this.write(entry));
    entry.outcome = run;
    this.queue = run;
    return run;
  }

  /** The document changes start from: the server's, as its preset draws it. */
  private base(): DiscoverLayout {
    return resolveLayout(this.confirmed ?? DEFAULT_LAYOUT);
  }

  private async write(entry: Pending): Promise<WriteOutcome> {
    entry.sent = true;
    try {
      let base = this.base();
      for (let attempt = 0; ; attempt++) {
        let next: DiscoverLayout;
        try {
          next = changed(base, entry.change);
        } catch (err) {
          if (err instanceof LayoutChangeError) return { ok: false, reason: 'refused', message: err.message };
          throw err;
        }
        if (sameLayout(next, base)) return { ok: true, layout: base };

        const res = entry.reset
          ? await this.deps.reset(base.rev)
          : await this.deps.put({ ...next, schema: DISCOVER_SCHEMA, rev: base.rev });
        if (res.ok) {
          this.confirm(res.layout);
          return { ok: true, layout: res.layout };
        }
        this.confirm(res.current);
        if (attempt >= 1) return { ok: false, reason: 'conflict', message: CONFLICT_MESSAGE };
        base = this.base();
      }
    } catch (err) {
      if (err instanceof DiscoverLayoutInvalidError) {
        return { ok: false, reason: 'invalid', problems: explainLayoutProblems(err.problems) };
      }
      return { ok: false, reason: 'failed', message: FAILED_MESSAGE };
    } finally {
      this.pending = this.pending.filter((p) => p !== entry);
      this.recompute();
    }
  }

  private confirm(layout: DiscoverLayout): void {
    this.confirmed = layout;
    this.deps.cache.write(layout);
    this.recompute();
  }

  private release(): void {
    this.stop();
    if (!this.holding) return;
    this.holding = false;
    this.recompute();
  }

  private recompute(emit = true): void {
    const layout = this.pending.reduce((l, p) => tryApply(l, p.change), this.base());
    this.snapshot = { layout, holding: this.holding, saving: this.pending.length > 0 };
    if (emit) this.listeners.forEach((l) => l());
  }
}

const CACHE_PREFIX = 'brewlytics:discover-layout:';

/** The per-profile copy, in `localStorage`; any storage error reads as no copy. */
export function profileLayoutCache(profileId: string | undefined): LayoutCache {
  const key = profileId ? `${CACHE_PREFIX}${profileId}` : null;
  return {
    read() {
      if (!key) return null;
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const v = JSON.parse(raw) as DiscoverLayout;
        return v?.schema === DISCOVER_SCHEMA && Array.isArray(v.sections) && Array.isArray(v.searches)
          && typeof v.rev === 'number' ? v : null;
      } catch {
        return null;
      }
    },
    write(layout) {
      if (!key) return;
      try {
        window.localStorage.setItem(key, JSON.stringify(layout));
      } catch {
        /* a full or blocked storage only costs the next visit its first frame */
      }
    },
  };
}
